import React from 'react';
import {
  Shield,
  Mic,
  Camera,
  Send,
  CheckCircle,
  AlertTriangle,
  TrendingUp,
  QrCode,
  FileText,
  User,
  Sparkles,
  ArrowRight,
  Download
} from 'lucide-react';

/**
 * オンボーディング用デモUI - Step 1: QRコード発行画面
 */
export const DemoQRManagement: React.FC = () => {
  return (
    <div className="h-full bg-slate-50 p-4 flex flex-col">
      {/* Header */}
      <div className="flex items-center gap-2 mb-4">
        <img src="/icon-512x512.png" alt="" className="h-7 w-7 rounded-lg" />
        <span className="font-bold text-sm text-slate-900">QRコード管理</span>
      </div>

      {/* QR List */}
      <div className="flex-1 space-y-2">
        {[
          { name: '第1工区 - 足場', count: 23, status: 'active' },
          { name: '資材置き場', count: 15, status: 'active' },
          { name: '重機エリア', count: 8, status: 'new' },
        ].map((qr, i) => (
          <div
            key={i}
            className={`bg-white rounded-lg p-3 border ${
              qr.status === 'new' ? 'border-safety-300 bg-safety-50' : 'border-slate-200'
            } flex items-center gap-3`}
          >
            <div className="w-10 h-10 bg-slate-100 rounded flex items-center justify-center">
              <QrCode size={20} className="text-slate-600" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-medium text-slate-800 truncate">{qr.name}</p>
              <p className="text-[10px] text-slate-500">{qr.count}件の報告</p>
            </div>
            {qr.status === 'new' && (
              <span className="text-[9px] bg-safety-500 text-white px-2 py-0.5 rounded-full">
                NEW
              </span>
            )}
          </div>
        ))}
      </div>

      {/* Create Button */}
      <button className="mt-4 w-full bg-safety-500 text-white py-2.5 rounded-lg text-sm font-bold flex items-center justify-center gap-2 shadow-lg shadow-safety-500/30">
        <QrCode size={16} />
        新規QRコード作成
      </button>
    </div>
  );
};

/**
 * オンボーディング用デモUI - Step 2: ワーカーホーム画面
 */
export const DemoWorkerHome: React.FC = () => {
  return (
    <div className="h-full bg-gradient-to-b from-safety-50 to-white p-4 flex flex-col">
      {/* Header */}
      <div className="text-center mb-6">
        <img src="/icon-512x512.png" alt="" className="inline-block w-12 h-12 rounded-xl mb-3 shadow-lg shadow-safety-500/30" />
        <h1 className="text-lg font-bold text-slate-900">安全ポスト＋</h1>
        <p className="text-xs text-slate-500 mt-1">第1工区 - 足場</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 gap-3 mb-6">
        <div className="bg-white rounded-xl p-3 shadow-sm border border-slate-100">
          <p className="text-[10px] text-slate-500">今月の報告</p>
          <p className="text-xl font-bold text-safety-600">23</p>
        </div>
        <div className="bg-white rounded-xl p-3 shadow-sm border border-slate-100">
          <p className="text-[10px] text-slate-500">改善実施率</p>
          <p className="text-xl font-bold text-emerald-600">87%</p>
        </div>
      </div>

      {/* CTA */}
      <div className="flex-1 flex flex-col justify-center">
        <button className="w-full bg-safety-500 text-white py-4 rounded-xl text-base font-bold shadow-lg shadow-safety-500/30 flex items-center justify-center gap-2">
          <FileText size={20} />
          報告する
        </button>
        <p className="text-center text-[10px] text-slate-400 mt-3">
          ログイン不要・匿名で報告できます
        </p>
      </div>
    </div>
  );
};

/**
 * オンボーディング用デモUI - Step 3: 報告入力画面
 */
export const DemoWorkerInput: React.FC = () => {
  return (
    <div className="h-full bg-white p-4 flex flex-col">
      {/* Header */}
      <div className="flex items-center gap-2 mb-4 pb-3 border-b border-slate-100">
        <img src="/icon-512x512.png" alt="" className="h-5 w-5 rounded" />
        <span className="text-sm font-bold text-slate-800">ヒヤリハット報告</span>
      </div>

      {/* Input Area */}
      <div className="flex-1 space-y-4">
        {/* Text Input */}
        <div className="bg-slate-50 rounded-xl p-3 border border-slate-200 min-h-[100px]">
          <p className="text-xs text-slate-500 italic">
            「足場の2段目で手すりがグラついていた。掴んだ瞬間ヒヤリとした。」
          </p>
        </div>

        {/* Input Methods */}
        <div className="flex gap-2">
          <button className="flex-1 bg-blue-50 text-blue-600 py-3 rounded-xl text-xs font-medium flex flex-col items-center gap-1 border border-blue-100">
            <Mic size={18} />
            音声入力
          </button>
          <button className="flex-1 bg-purple-50 text-purple-600 py-3 rounded-xl text-xs font-medium flex flex-col items-center gap-1 border border-purple-100">
            <Camera size={18} />
            写真追加
          </button>
        </div>

        {/* Photo Preview */}
        <div className="flex gap-2">
          <div className="w-16 h-16 bg-slate-200 rounded-lg flex items-center justify-center relative">
            <Camera size={16} className="text-slate-400" />
            <div className="absolute -top-1 -right-1 w-4 h-4 bg-purple-500 rounded-full flex items-center justify-center">
              <CheckCircle size={10} className="text-white" />
            </div>
          </div>
        </div>
      </div>

      {/* Submit Button */}
      <button className="w-full bg-safety-500 text-white py-3 rounded-xl text-sm font-bold flex items-center justify-center gap-2 shadow-lg shadow-safety-500/30 mt-4">
        <Sparkles size={16} />
        AIで分析・送信
      </button>
    </div>
  );
};

/**
 * オンボーディング用デモUI - Step 4: AI分析結果画面
 */
export const DemoAIAnalysis: React.FC = () => {
  return (
    <div className="h-full bg-slate-50 p-4 flex flex-col">
      {/* Header */}
      <div className="text-center mb-4">
        <div className="inline-flex items-center justify-center w-10 h-10 bg-safety-100 rounded-full mb-2">
          <Sparkles size={18} className="text-safety-600" />
        </div>
        <h2 className="text-sm font-bold text-slate-800">AI分析結果</h2>
      </div>

      {/* Analysis Card */}
      <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-100 flex-1 space-y-3">
        {/* Title */}
        <div>
          <p className="text-[10px] text-slate-400 mb-1">タイトル</p>
          <p className="text-sm font-bold text-slate-800">足場の手すり緩み発見</p>
        </div>

        {/* Risk Level */}
        <div className="flex items-center gap-2">
          <span className="text-[10px] text-slate-400">危険度:</span>
          <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-red-100 text-red-700 rounded-full text-[10px] font-bold">
            <AlertTriangle size={10} />
            High
          </span>
        </div>

        {/* 4M Analysis */}
        <div>
          <p className="text-[10px] text-slate-400 mb-1">4M分類</p>
          <div className="flex gap-1">
            <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded text-[10px]">設備</span>
            <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded text-[10px]">#足場</span>
          </div>
        </div>

        {/* Anonymized Text */}
        <div className="bg-slate-50 rounded-lg p-3">
          <p className="text-[10px] text-slate-400 mb-1">匿名化済みテキスト</p>
          <p className="text-xs text-slate-700">
            足場の2段目で手すりがグラついていた。掴んだ瞬間ヒヤリとした。
          </p>
        </div>

        {/* Countermeasure */}
        <div className="bg-amber-50 rounded-lg p-3 border border-amber-100">
          <p className="text-[10px] text-amber-600 font-bold mb-1">AI提案の改善策</p>
          <p className="text-xs text-amber-800">
            手すりの固定ボルトを点検し、緩みがあれば締め直す。定期点検項目に追加を推奨。
          </p>
        </div>
      </div>

      {/* Confirm Button */}
      <button className="w-full bg-safety-500 text-white py-3 rounded-xl text-sm font-bold flex items-center justify-center gap-2 shadow-lg shadow-safety-500/30 mt-4">
        <Send size={16} />
        この内容で報告する
      </button>
    </div>
  );
};

/**
 * オンボーディング用デモUI - ミニダッシュボード
 */
export const DemoMiniDashboard: React.FC = () => {
  return (
    <div className="bg-slate-100 p-3 h-full overflow-hidden flex flex-col font-sans">
      {/* Header */}
      <div className="flex justify-between items-center mb-3 pb-2 border-b border-slate-200">
        <div className="flex items-center space-x-2">
          <img src="/icon-512x512.png" alt="" className="h-5 w-5 rounded" />
          <span className="font-bold text-[10px] text-slate-900">安全ポスト＋</span>
        </div>
        <div className="flex items-center gap-1">
          <User size={12} className="text-slate-400" />
          <span className="text-[9px] text-slate-500">管理者</span>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 gap-2 mb-3">
        <div className="bg-white p-2 rounded-lg shadow-sm">
          <p className="text-[8px] text-slate-500">未対応High</p>
          <p className="text-lg font-bold text-red-500">2</p>
        </div>
        <div className="bg-white p-2 rounded-lg shadow-sm">
          <p className="text-[8px] text-slate-500">改善実施率</p>
          <p className="text-lg font-bold text-emerald-600">87%</p>
        </div>
      </div>

      {/* Mini Chart */}
      <div className="bg-white p-2 rounded-lg shadow-sm mb-3 flex-1">
        <p className="text-[8px] font-medium text-slate-700 mb-2">投稿推移</p>
        <div className="h-12 flex items-end gap-1">
          {[3, 5, 4, 7, 6, 8, 9].map((h, i) => (
            <div
              key={i}
              className="flex-1 bg-safety-500 rounded-t"
              style={{ height: `${h * 10}%`, opacity: 0.5 + (i * 0.07) }}
            />
          ))}
        </div>
      </div>

      {/* Recent Reports */}
      <div className="bg-white rounded-lg p-2 shadow-sm">
        <p className="text-[8px] font-medium text-slate-700 mb-2">最新の報告</p>
        <div className="space-y-1.5">
          {[
            { title: '足場の手すり緩み', risk: 'high' },
            { title: '通路に資材放置', risk: 'medium' },
          ].map((item, i) => (
            <div key={i} className="flex items-center gap-2 p-1.5 bg-slate-50 rounded">
              <div className={`w-1.5 h-1.5 rounded-full ${
                item.risk === 'high' ? 'bg-red-500' : 'bg-yellow-500'
              }`} />
              <span className="text-[9px] text-slate-700 truncate flex-1">{item.title}</span>
              <ArrowRight size={10} className="text-slate-400" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

/**
 * FAQアコーディオンコンポーネント
 */
interface FAQItem {
  question: string;
  answer: string;
}

export const FAQAccordion: React.FC<{ items: FAQItem[] }> = ({ items }) => {
  const [openIndex, setOpenIndex] = React.useState<number | null>(0);

  return (
    <div className="space-y-3">
      {items.map((item, index) => (
        <div
          key={index}
          className="bg-white rounded-xl border border-slate-200 overflow-hidden transition-all"
        >
          <button
            onClick={() => setOpenIndex(openIndex === index ? null : index)}
            className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-slate-50 transition"
          >
            <span className="font-medium text-slate-800">{item.question}</span>
            <span className={`text-safety-500 transition-transform ${
              openIndex === index ? 'rotate-180' : ''
            }`}>
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </span>
          </button>
          <div className={`overflow-hidden transition-all duration-300 ${
            openIndex === index ? 'max-h-96' : 'max-h-0'
          }`}>
            <div className="px-6 pb-4 text-slate-600 text-sm leading-relaxed">
              {item.answer}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};
